import { RecordType } from '@wiztime/core';

/**
 * Protocol constants of the sync layer (docs/architecture.md section 6). Every value here is part
 * of the wire or key-derivation contract: changing one breaks compatibility with existing spaces.
 */

/** Argon2id salt (UTF-8 bytes of this string). */
export const KDF_SALT = 'wiztime/v1/salt';

/** Argon2id cost parameters of the passphrase-to-master-key step. */
export const ARGON2_PARAMS = {
  /** Memory in KiB (64 MiB). */
  memoryKiB: 65_536,
  iterations: 3,
  parallelism: 1,
  /** Output length in bytes. */
  hashLength: 32,
} as const;

/** HKDF-SHA256 `info` labels, one per derived key. */
export const HkdfInfo = {
  SpaceId: 'wiztime/space-id/v1',
  Enc: 'wiztime/enc/v1',
  Auth: 'wiztime/auth/v1',
  Rid: 'wiztime/rid/v1',
} as const;

export const SPACE_ID_BYTES = 16;
export const AUTH_TOKEN_BYTES = 32;
/** Length of the HMAC-SHA256 record-identifier key in bits. */
export const RID_KEY_BITS = 256;

/** Ciphertext format version written as the first byte of every ciphertext. */
export const CIPHERTEXT_VERSION = 1;
export const IV_BYTES = 12;

export const PASSPHRASE_WORD_COUNT = 12;
/** 12 BIP39 words carry 128 bits of entropy plus a 4-bit checksum. */
export const PASSPHRASE_ENTROPY_BITS = 128;

/** Server limits the client must respect (architecture.md section 6.2). */
export const MAX_PUSH_RECORDS = 500;
/** Client-side body budget, below the server's 2 MB limit. */
export const MAX_PUSH_BODY_BYTES = 1_500_000;
export const MAX_CIPHERTEXT_CHARS = 65_536;
export const PULL_PAGE_SIZE = 500;
export const REQUEST_TIMEOUT_MS = 10_000;

/** Name of the device-local configuration entry (data dictionary section 7). */
export const SYNC_CONFIG_KEY = 'wiztime.sync';
/** Device-local identifier of the space this device last synced with; survives logout (data dictionary section 7). */
export const LAST_SPACE_ID_KEY = 'wiztime.lastSpaceId';
/** Dexie database holding the derived keys. */
export const KEY_DB_NAME = 'wiztime-keys';

/** Automatic sync triggers (architecture.md section 6.3). */
export const LOCAL_CHANGE_DEBOUNCE_MS = 2_000;
export const SYNC_INTERVAL_MS = 60_000;
export const MAX_BACKOFF_MS = 5 * 60_000;
export const BASE_BACKOFF_MS = 2_000;
/** Cross-tab lock name guarding one sync run at a time per browser profile. */
export const SYNC_LOCK_NAME = 'wiztime-sync';

/** Account login (docs/architecture-account.md section 2). The Argon2id parameters are sent by the server and range-checked. */
export const ACCOUNT_KDF_SALT_BYTES = 16;
export const ACCOUNT_KDF_DEFAULT = { m: 65_536, t: 3, p: 1 } as const;
/** Accepted ranges of server-supplied login KDF parameters; m is in KiB. */
export const ACCOUNT_KDF_RANGE = {
  mMin: 65_536,
  mMax: 1_048_576,
  tMin: 3,
  tMax: 10,
  pMin: 1,
  pMax: 16,
} as const;
/** Length in bytes of the login root key, the login proof and the recovery proof. */
export const ACCOUNT_KEY_BYTES = 32;

/** HKDF-SHA256 `info` labels of the account keys (login root key and master key respectively). */
export const AccountHkdfInfo = {
  LoginProof: 'wiztime/login-proof/v1',
  Wrap: 'wiztime/wrap/v1',
  RecoveryProof: 'wiztime/account-recovery/v1',
} as const;

/** Format version written as the first byte of the wrapped recovery code. */
export const WRAP_VERSION = 1;
/** Prefix of the additional authenticated data of the wrapped recovery code; followed by `|{spaceId}`. */
export const WRAP_AAD_PREFIX = 'wiztime/wrap/v1';

/** How this device syncs (data dictionary section 12, `SyncMode`). */
export const SyncMode = {
  /** Only the 12-word sync passphrase. */
  Passphrase: 'passphrase',
  /** Logged in to an account that keeps the recovery code. */
  Account: 'account',
} as const;
export type SyncMode = (typeof SyncMode)[keyof typeof SyncMode];

/** The user's answer when activating sync on a device that holds data of another space. */
export const ForeignLocalDataDecision = {
  /** Delete the local data, then activate (the device only pulls). */
  Clear: 'clear',
  /** Keep the local data and upload it to the new space. */
  Merge: 'merge',
  /** Do not activate. */
  Cancel: 'cancel',
} as const;
export type ForeignLocalDataDecision =
  (typeof ForeignLocalDataDecision)[keyof typeof ForeignLocalDataDecision];

/** Why the data on this device is "foreign" when sync is activated. */
export const ForeignLocalDataReason = {
  /** A different space was synced on this device before (the remembered space differs). */
  OtherSpace: 'otherSpace',
  /** No remembered space, but records written by other devices (an imported backup or an old sync). */
  UnknownOrigin: 'unknownOrigin',
} as const;
export type ForeignLocalDataReason = (typeof ForeignLocalDataReason)[keyof typeof ForeignLocalDataReason];

/** Path prefix of the account endpoints (docs/architecture-account.md section 3.2). */
export const ACCOUNTS_API_PATH = '/api/v1/accounts';

/**
 * The record types this build understands. A pulled record of any other type is skipped (a newer
 * client wrote it). The set is stored as `knownTypes` in the sync configuration so that a later build that
 * knows more types pulls everything again (data dictionary section 7, `wiztime.sync.knownTypes`).
 */
export const KNOWN_RECORD_TYPES: readonly string[] = Object.values(RecordType);
